import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AlertButtonColor, AlertButtonStyle } from "@obg/mat.ui.core";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import { CommonState } from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	CORE_ROUTE,
	AppError,
	MarketSelector,
	MessageService,
	normalizePersonalNumber,
	PERSONAL_NUMBER_CODES,
	ProductType,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { DeviceConfigService } from "@obg/ngx.global";
import { RouteSelector, RouteAction } from "@obg/ngx.router";
import { asyncScheduler, merge, of } from "rxjs";
import { catchError, map, observeOn, switchMap, withLatestFrom } from "rxjs/operators";

import { RegistrationFeatureState } from "../../state";
import { REGISTRATION_ACTION_TYPE, RegistrationAction } from "../registration.action";
import { PARTIAL_FLOW_APP_ERROR_CODES } from "../registration.const";
import { Credential, RegistrationResponse, UserIdentity } from "../registration.model";

import {
	REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE,
	RegistrationWithPersonalNumberAction,
} from "./registration-with-personal-number.action";
import {
	PersonalNumberInformationLevel,
	RegistrationWithPersonalNumberCompletePayload,
	RegistrationWithPersonalNumberSimplePayload,
} from "./registration-with-personal-number.model";
import { RegistrationWithPersonalNumberSelector } from "./registration-with-personal-number.selector";
import { RegistrationWithPersonalNumberService } from "./registration-with-personal-number.service";

@Injectable()
export class RegistrationWithPersonalNumberEffect {
	getRegistrationTokenSimpleFlow$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenSimpleFlow),
			map((x: ActionWithPayload<RegistrationWithPersonalNumberSimplePayload>) => x.payload),
			withLatestFrom(
				this.store.select(this.registrationWithPersonalNumberSelector.getPersonalNumber()),
				this.store.select(this.marketSelector.getCurrent()),
				this.store.select<{ product: ProductType | undefined } | undefined>(RouteSelector.getQueryParams)
			),
			switchMap(([request, personalNumber, market, params]) =>
				this.service.registerSimpleFlow(request, personalNumber!, market, params?.product).pipe(
					map((response: RegistrationResponse) =>
						this.action.getRegistrationTokenSimpleFlowSuccess(
							response,
							this.getCredentials(request.credential)
						)
					),
					catchError((response: AppError) => of(this.action.getRegistrationTokenSimpleFlowFail(response)))
				)
			)
		)
	);

	getRegistrationTokenCompleteFlow$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenCompleteFlow),
			map((x: ActionWithPayload<RegistrationWithPersonalNumberCompletePayload>) => x.payload),
			withLatestFrom(
				this.store.select(this.registrationWithPersonalNumberSelector.getPersonalNumber()),
				this.store.select(this.marketSelector.getCurrent()),
				this.store.select<{ product: ProductType | undefined } | undefined>(RouteSelector.getQueryParams)
			),
			switchMap(([request, personalNumber, market, params]) =>
				this.service.registerCompleteFlow(request, personalNumber!, market, params?.product).pipe(
					map((response: RegistrationResponse) =>
						this.action.getRegistrationTokenCompleteFlowSuccess(
							response,
							this.getCredentials(request.credential)
						)
					),
					catchError((response: AppError) => of(this.action.getRegistrationTokenCompleteFlowFail(response)))
				)
			)
		)
	);

	verifyPersonalNumber$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.verifyPersonalNumber),
			map((x: ActionWithPayload<string>) => normalizePersonalNumber(x.payload)),
			withLatestFrom(this.store.select(this.registrationWithPersonalNumberSelector.getState())),
			switchMap(([personalNumber, personalNumberState]) =>
				personalNumber === personalNumberState.submittedPersonalNumber &&
				personalNumberState.submittedPersonalNumberInformationLevel
					? of(
							this.action.verifyPersonalNumberSuccess({
								personalNumber,
								informationLevel: personalNumberState.submittedPersonalNumberInformationLevel,
							})
						)
					: this.service.verifyPersonalNumber(personalNumber).pipe(
							map(({ informationLevel }) => {
								const { NoData, Partial } = PersonalNumberInformationLevel;
								const processedInformationLevel =
									informationLevel === NoData ? Partial : informationLevel;
								return this.action.verifyPersonalNumberSuccess({
									informationLevel: processedInformationLevel,
									personalNumber,
								});
							}),
							catchError((error: AppError) => {
								if (error.code === PERSONAL_NUMBER_CODES.personalNumberInUseError) {
									this.openPersonalCodeInUseDialog();
									return of(
										this.action.verifyPersonalNumberSuccess({
											informationLevel: PersonalNumberInformationLevel.Partial,
											personalNumber,
										})
									);
								}

								if (
									error.code === PERSONAL_NUMBER_CODES.validationError &&
									error.data.errors &&
									error.data.errors.length
								) {
									return of(
										this.action.verifyPersonalNumberFail(new Error(error.data.errors[0].code))
									);
								}
								if (
									error.code === PERSONAL_NUMBER_CODES.serviceUnavailableError ||
									PARTIAL_FLOW_APP_ERROR_CODES.includes(error.type)
								) {
									return of(
										this.action.verifyPersonalNumberSuccess({
											informationLevel: PersonalNumberInformationLevel.Partial,
											personalNumber,
										})
									);
								}
								return of(this.action.verifyPersonalNumberFail(error));
							}),
							map((action) =>
								batchActions(
									this.registrationAction.clearRegistrationDetails(),
									this.registrationAction.resetRegistrationState(),
									action
								)
							)
						)
			)
		)
	);

	clear$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_ACTION_TYPE.registrationSuccess),
			observeOn(asyncScheduler), // run in nextTick as the data is used by other effects on same action
			map(() => this.action.resetRegistrationDetails())
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.verifyPersonalNumberFail]:
					REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.verifyPersonalNumber,
			}),
			this.messageService.mapClearByContext$({
				[REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.verifyPersonalNumber]:
					REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.verifyPersonalNumber,
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: RegistrationWithPersonalNumberAction,
		private service: RegistrationWithPersonalNumberService,
		private registrationWithPersonalNumberSelector: RegistrationWithPersonalNumberSelector,
		private marketSelector: MarketSelector,
		private store: Store<RegistrationFeatureState & CommonState>,
		private messageService: MessageService,
		private registrationAction: RegistrationAction,
		private alertService: AbstractTranslatableAlertService,
		private deviceConfigService: DeviceConfigService
	) {}

	private getCredentials(credential: UserIdentity): Credential | undefined {
		const { username, email, password } = credential;
		if (password) {
			return {
				username,
				email,
				password,
			};
		}
		return undefined;
	}

	private openPersonalCodeInUseDialog() {
		let queryParams = {};
		const isDesktop = this.deviceConfigService.isDesktop();
		this.alertService.open(
			{
				hideClose: false,
				buttons: [
					{
						color: isDesktop ? AlertButtonColor.Accent : AlertButtonColor.Primary,
						style: isDesktop ? AlertButtonStyle.Flat : AlertButtonStyle.Transparent,
						text: "user-summary.login",
						handler: () => {
							if (isDesktop) {
								queryParams = {
									loginFromRegistration: true,
								};
							}
							this.store.dispatch(
								batchActions(
									RouteAction.navigate(CORE_ROUTE.home, {}),
									RouteAction.navigate(CORE_ROUTE.auth.login, {}, { queryParams })
								)
							);
						},
					},
				],
				id: "NON_UNIQUE_ACCOUNT",
				title: "register.title.no-unique-account-error",
				message: "register.message.no-unique-account-error",
			},
			[TranslationGroup.Common]
		);
	}
}
